import { memo, useEffect, useMemo, useRef, useState } from "react";
import { bool, func, object, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { BreakdownColumns } from "@repo/utils";
import { USER_COMPANY_SETTINGS } from "@client";
import { setCompanySettingsQuery } from "@query";
import { CELL_WIDTHS, PRO_TIP_PREVIEWS } from "@utils";
import { useStores } from "@hooks";
import { useEstimateEditorStore, useEstimateStructure } from "@tools";
import { Alert } from "@components";
import useStyle from "./CellRescale.style";
import classnames from "classnames";

export const CellRescale = memo(({
  colName,
  cellRef,
  columnSettings,
  widerPaddings,
}) => {
  const { t } = useTranslation();
  const { userStore } = useStores();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const classes = useStyle();
  
  const widthRef = useRef({});
  const handleRef = useRef();
  const isDragged = useRef(false);
  
  const [ resizeAlertVisible, showResizeAlert ] = useState(false);
  
  const minWidth = useMemo(() => (
    columnSettings.name === BreakdownColumns.LABELS ? CELL_WIDTHS.LABELS_MIN : CELL_WIDTHS.TITLE_MIN
  ), [ ])
  
  const handleMouseMove = (e) => {
    if (!widthRef.current || !cellRef?.current || !isDragged.current)
      return;
    
    const newWidth = Math.round(
      Math.max(
        Math.min(
          e.clientX - cellRef?.current.getBoundingClientRect().x,
          widthRef.current.maxWidth
        ),
        minWidth
      )
    );
    
    cellRef.current.style.maxWidth = `${newWidth}px`;
    widthRef.current.newWidth = newWidth;
    
    if(columnSettings.nextVisiblePinnedColumn && typeof widthRef.current.nextColumnWidth === "number") {
      const nextCellWidth = widthRef.current.nextColumnWidth - (newWidth - widthRef.current.columnWidth);
      
      if(widthRef.current.ctrl)
        columnSettings.nextVisiblePinnedColumn.setWidth(nextCellWidth);
      else
        structure.setBreakdownColumnWidth(columnSettings.nextVisiblePinnedColumn.id, nextCellWidth);
    }
    
    if(widthRef.current.ctrl)
      columnSettings.setWidth(newWidth);
    else
      structure.setBreakdownColumnWidth(colName, newWidth);
    
    if(widthRef.current.ctrl !== (e.ctrlKey || e.metaKey)) {
      handleMouseUp();
    }
  }
  
  const handleMouseDown = (e) => {
    if(e.button !== 0) return;
    styleHandle(true);
    
    widthRef.current = {
      ctrl: e.ctrlKey || e.metaKey,
      columnWidth: columnSettings.width,
      nextColumnWidth: columnSettings.nextVisiblePinnedColumn?.width,
      maxWidth: columnSettings.nextVisiblePinnedColumn
        ? (
          (columnSettings.width || 0) + (columnSettings.nextVisiblePinnedColumn?.width || 0) - (
            columnSettings.nextVisiblePinnedColumn.name === BreakdownColumns.LABELS ? CELL_WIDTHS.LABELS_MIN : CELL_WIDTHS.TITLE_MIN
          )
        )
        : CELL_WIDTHS.TITLE_MAX
    };
    
    structure.historyManager.startGroup();
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };
  
  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
    
    if(!widthRef.current || !isDragged.current) return;
    
    cellRef.current.style.maxWidth = "";
    
    if(widthRef.current.newWidth) {
      if(widthRef.current.ctrl)
        columnSettings.setWidth(widthRef.current.newWidth);
      else
        structure.setBreakdownColumnWidth(colName, widthRef.current.newWidth);
    }
    
    structure.historyManager.stopGroup();
    
    if(widthRef.current.newWidth && !widthRef.current.ctrl && !editorStore?.blockedResizeAlert)
      setTimeout(() => showResizeAlert(true), 100);
    
    widthRef.current = undefined;
    styleHandle(false);
  }
  
  const styleHandle = (active=false) => {
    isDragged.current = active;
    if(!handleRef.current)
      return;
    
    handleRef.current.style.opacity = active ? "1" : "";
  }
  
  const handleRiskAlertClose = async (e, lock) => {
    showResizeAlert(false);
    if (lock) {
      editorStore.blockedResizeAlert = true;
      await setCompanySettingsQuery(USER_COMPANY_SETTINGS.RESIZE_ALERT(userStore.userUuid), true)
    }
  };
  
  useEffect(() => {
    return () => {
      document.removeEventListener("mouseup", handleMouseUp, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
    }
  })
  
  return (
    <div className="relative">
      <div
        ref={handleRef}
        className={classnames(
          "noDrag resizeHandle py-1 absolute",
          classes.handle,
          widerPaddings && classes.handleFurther,
        )}
        onMouseDown={handleMouseDown}
      />
      {
        resizeAlertVisible &&
        <Alert
          isOpen={resizeAlertVisible}
          title={t("views.editor.resize_alert.title")}
          acceptText={t("common.close")}
          onAccept={handleRiskAlertClose}
          useLockCheckbox
          width={600}
        >
          {t("views.editor.resize_alert.text")}
          <video
            autoPlay loop
            className="border-solid border-light w-full rounded mt-6"
          >
            <source
              src={PRO_TIP_PREVIEWS.resizeBreakdown}
              type="video/mp4"
            />
          </video>
        </Alert>
      }
    </div>
  );
})

CellRescale.propTypes = {
  colName: string.isRequired,
  cellRef: object.isRequired,
  columnSettings: shape({
    setWidth: func.isRequired,
  }).isRequired,
  widerPaddings: bool,
}